import { observable } from 'mobx';
import SalonApi from '../../api/SalonApi';
import Salon, { ISalon } from '../../domain/entities/Salon/Salon';
import SalonFormStore from '../../domain/entities/SalonFormStore/SalonFormStore';
import UserSalon, { IUserSalon } from '../../domain/entities/Salon/UserSalon';
import ConfigRepository from '../ConfigRepository';
import normalizeApiErrors from '../../helpers/normalizeApiErrors';
import NotificationsCollection from '../../domain/entities/Notification/NotificationsCollection';
import { NOTIFICATION_GROUP } from '../../constants/Notifications';
import { SALON_COMMISSION } from '../../constants/Errors';
import { FIELD_TYPE } from '../../constants/Constants';

interface ISalonRepositoryConfig {
  salonApi: SalonApi;
  salonFormStore: SalonFormStore;
  configRepository: ConfigRepository;
  notificationsCollection: NotificationsCollection;
}

class SalonRepository {
  api: SalonApi;

  configRepository: ConfigRepository;

  notificationsCollection: NotificationsCollection;

  @observable userSalon: UserSalon = new UserSalon();

  @observable savedUserSalon: UserSalon = new UserSalon();

  @observable public readonly formStore: SalonFormStore;

  @observable isUserSalonSet = false;

  @observable isSalonCommissionToggleChecked = false;

  constructor(config: ISalonRepositoryConfig) {
    this.api = config.salonApi;
    this.configRepository = config.configRepository;
    this.formStore = config.salonFormStore;
    this.notificationsCollection = config.notificationsCollection;
  }

  private hasUserSalonChanged(): boolean {
    return this.userSalon.selectedSalonId !== this.savedUserSalon.selectedSalonId
      || this.formStore.values[FIELD_TYPE.STYLIST_NAME] !== this.savedUserSalon.stylistName
      || this.userSalon.isPrivacyPolicyAccepted !== this.savedUserSalon.isPrivacyPolicyAccepted;
  }

  getSalonsByZipcode = async (zipcode: string): Promise<Salon[]> | null => {
    const response = await this.api.getSalons(zipcode);
    return response.salons.map((s: ISalon) => Salon.fromJSON(s));
  };

  saveUserSalon = async (): Promise<void> => {
    const config = await this.configRepository.getConfig();
    if (config.salonCommissionEnabled) {
      const { isPrivacyPolicyAccepted, selectedSalonId } = this.userSalon;
      const stylistName = this.formStore.values[FIELD_TYPE.STYLIST_NAME];
      const requestData = { isPrivacyPolicyAccepted, selectedSalonId, stylistName };

      if (this.hasUserSalonChanged()) {
        this.notificationsCollection.clearNotifications(NOTIFICATION_GROUP.SALON);
        try {
          await this.api.updateSalon(requestData);
        } catch (errors) {
          const normalizedErrors = normalizeApiErrors(errors, NOTIFICATION_GROUP.SALON);
          this.notificationsCollection.addNotifications(normalizedErrors);
          throw new Error(SALON_COMMISSION.INVALID_NAME);
        }

        this.savedUserSalon.update(requestData);
        this.userSalon.update(requestData);
      }
    }
  };

  setUserSalon = (userSalonData: IUserSalon): void => {
    this.userSalon = UserSalon.fromJSON(userSalonData);
    this.savedUserSalon = UserSalon.fromJSON(userSalonData);
    this.isUserSalonSet = true;
  };

  setToggleChecked = (checked: boolean): void => {
    this.isSalonCommissionToggleChecked = checked;
  };
}

export default SalonRepository;
